'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';

interface KetchTagProps {
  orgCode: string;
  propertyCode: string;
}

export default function KetchTag({ orgCode, propertyCode }: KetchTagProps): React.ReactNode {
  // Check if the current route includes specific urls to blacklist
  const pathname = usePathname();
  const blacklistedUrls = ['/privacy/mobile/', '/privacy/lr/', '/privacy/web/workforce', '/privacy/web/subscriber'];
  const isBlacklistedUrl = blacklistedUrls.some((url) => pathname.startsWith(url));

  /**
   * The Ketch script tag should typically be disabled in local development.
   * Ketch is a consent management platform used for privacy compliance (like GDPR/CCPA),
   * and running it locally is unnecessary and can slow down development or cause unwanted prompts.
   * It’s best to enable Ketch only in staging and production environments.
   */
  const isLocalDevelopment = process.env.NODE_ENV === 'development';

  if (isBlacklistedUrl || isLocalDevelopment) {
    return null;
  }

  // Only render the Script component if the current route is not in the blacklistedUrls
  return (
    <Script
      dangerouslySetInnerHTML={{
        __html: `!function(){
          window.semaphore=window.semaphore||[],
          window.ketch=function(){
            window.semaphore.push(arguments)
          };
          var e=document.createElement("script");
          e.type="text/javascript",
          e.src="https://global.ketchcdn.com/web/v3/config/${orgCode}/${propertyCode}/boot.js",
          e.defer=e.async=!0,
          document.getElementsByTagName("head")[0].appendChild(e)}();`,
      }}
      id="KetchTag"
    />
  );
}
